import { useState } from 'react';
import { ExternalLink, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface StickyAffiliateCTAProps {
  firmName: string;
  ctaLabel: string;
  targetUrl: string;
  couponCode?: string | null;
  discountDescription?: string | null;
  onCTAClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export function StickyAffiliateCTA({
  firmName,
  ctaLabel,
  targetUrl,
  couponCode,
  discountDescription,
  onCTAClick,
}: StickyAffiliateCTAProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!couponCode) return;
    navigator.clipboard.writeText(couponCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'coupon_copy', {
        firm_name: firmName,
        coupon_code: couponCode,
      });
    }
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-[0_-4px_16px_rgba(0,0,0,0.12)]">
      <div className="container mx-auto px-4 py-3 flex items-center gap-3">
        {couponCode ? (
          <button
            onClick={handleCopy}
            className="shrink-0 flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2 text-sm cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
            title="Click to copy"
          >
            <Tag className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            {copied ? (
              <span className="text-emerald-700 dark:text-emerald-300 font-medium">Copied!</span>
            ) : (
              <span className="font-mono font-bold text-emerald-700 dark:text-emerald-300">{couponCode}</span>
            )}
          </button>
        ) : (
          <span className="shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[120px]">
            {firmName}
          </span>
        )}

        <Button
          size="lg"
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-md shadow-orange-500/20 text-base"
          asChild
        >
          <a
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => onCTAClick(e)}
          >
            {ctaLabel}
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </div>
    </div>
  );
}
